import React, { useEffect, useState, useRef, Children, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShieldAlert, Brain, Flame, EyeOff, CheckCircle2, XCircle, Clock, Sparkles, ChevronDown, Lock, Star, ShieldCheck } from 'lucide-react';
import { Button } from '../components/Button';
import { SalesNotification } from '../components/SalesNotification';
import { trackViewContent, handlePurchaseCTA } from '../utils/metaPixel';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-brown/10 py-5" data-id="element-13">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left focus:outline-none group" data-id="element-14">
        <span className="font-serif text-xl md:text-2xl text-brown pr-4 group-hover:text-terracotta transition-colors" data-id="element-15">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-16">
          <ChevronDown className="w-6 h-6 text-terracotta flex-shrink-0" data-id="element-17" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-18">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-19">
            <p className="pt-4 text-brown/80 text-lg leading-relaxed" data-id="element-20">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const getTimeUntilMidnightEST = () => {
      // Get current time in US Eastern using Intl API
      const nowEST = new Date(new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York'
      }));
      // Midnight tonight Eastern
      const midnightEST = new Date(nowEST);
      midnightEST.setHours(24, 0, 0, 0);
      const diffMs = midnightEST.getTime() - nowEST.getTime();
      const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor(totalSeconds % 3600 / 60);
      const s = totalSeconds % 60;
      return {
        hours: h,
        minutes: m,
        seconds: s
      };
    };
    setTimeLeft(getTimeUntilMidnightEST());
    const interval = setInterval(() => {
      setTimeLeft(getTimeUntilMidnightEST());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="mt-8 mb-2 flex flex-col items-center gap-2" data-id="element-21">
      <p className="text-sm text-brown/50 font-medium tracking-wide uppercase" data-id="element-22">
        Esta oferta expira en
      </p>
      <div className="flex items-center gap-3" data-id="element-23">
        <div className="flex flex-col items-center" data-id="element-24">
          <span className="text-3xl md:text-4xl font-serif font-bold text-brown tabular-nums" data-id="element-25">
            {pad(timeLeft.hours)}
          </span>
          <span className="text-[10px] text-brown/40 uppercase tracking-wider mt-1" data-id="element-26">
            Horas
          </span>
        </div>
        <span className="text-2xl text-brown/30 font-bold -mt-4" data-id="element-27">:</span>
        <div className="flex flex-col items-center" data-id="element-28">
          <span className="text-3xl md:text-4xl font-serif font-bold text-brown tabular-nums" data-id="element-29">
            {pad(timeLeft.minutes)}
          </span>
          <span className="text-[10px] text-brown/40 uppercase tracking-wider mt-1" data-id="element-30">
            Min
          </span>
        </div>
        <span className="text-2xl text-brown/30 font-bold -mt-4" data-id="element-31">:</span>
        <div className="flex flex-col items-center" data-id="element-32">
          <span className="text-3xl md:text-4xl font-serif font-bold text-terracotta tabular-nums" data-id="element-33">
            {pad(timeLeft.seconds)}
          </span>
          <span className="text-[10px] text-brown/40 uppercase tracking-wider mt-1" data-id="element-34">
            Seg
          </span>
        </div>
      </div>
    </div>;
}
function PricingBonusDetails() {
  const [isOpen, setIsOpen] = useState(false);
  const bonuses = [{
    number: 1,
    title: 'Guía de Recursos Sensoriales STEAM',
    desc: 'Crea tu kit completo por menos de $20 esta tarde.',
    image: 'https://cdn.magicpatterns.com/uploads/k5Xpit1v4dQdPU4b29fDf4/Bono_1.png'
  }, {
    number: 2,
    title: 'Intervención Inmediata',
    desc: '3 experimentos exactos para los primeros 90 segundos de cualquier rabieta.',
    image: 'https://cdn.magicpatterns.com/uploads/uYvFyL3tY3MCiQkiG91PhX/Bono_2.png'
  }, {
    number: 3,
    title: 'El Detective Emocional',
    desc: 'Identifica la causa real de cada rabieta en 10 segundos y actúa con precisión.',
    image: 'https://cdn.magicpatterns.com/uploads/aT79xaNompigEaPv8HWTJr/Bono_3.png'
  }, {
    number: 4,
    title: 'Kit para Rabietas en Público',
    desc: '3 técnicas invisibles para recuperar la calma sin miradas incómodas.',
    image: 'https://cdn.magicpatterns.com/uploads/5299TuU5e9CJ5fmMmaZcph/Bono_4.png'
  }, {
    number: 5,
    title: 'Tracker de Patrones',
    desc: 'El mapa para anticiparte a las rabietas antes de que exploten.',
    image: 'https://cdn.magicpatterns.com/uploads/oEMnX9RLCFHnfrqXLZNw75/Bono_5.png'
  }];
  return <div className="mt-4" data-id="element-35">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-center gap-1.5 mx-auto text-sm text-terracotta font-medium hover:underline focus:outline-none" data-id="element-36">
        <span data-id="element-37">{isOpen ? 'Ocultar detalles' : 'Leer más sobre los bonos'}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-38">
          <ChevronDown className="w-4 h-4" data-id="element-39" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-40">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-41">
            <div className="pt-6 space-y-4" data-id="element-42">
              {bonuses.map(bonus => <div key={bonus.number} className="flex items-center gap-4 p-4 bg-cream rounded-xl" data-id="element-43">
                  <img src={bonus.image} alt={bonus.title} className="w-16 h-20 object-cover rounded-lg shadow-sm shrink-0" loading="lazy" data-id="element-44" />
                  <div data-id="element-45">
                    <p className="text-xs font-bold uppercase tracking-wider text-brown/40" data-id="element-46">
                      Bonus #{bonus.number}
                    </p>
                    <h4 className="font-serif text-base text-brown mt-0.5" data-id="element-47">
                      {bonus.title}
                    </h4>
                    <p className="text-brown/60 text-sm mt-0.5" data-id="element-48">{bonus.desc}</p>
                  </div>
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function LandingPage() {
  const pricingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pricingRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        trackViewContent();
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-cream overflow-hidden" data-id="element-49">
      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-12 md:pt-12 md:pb-12 px-6 overflow-hidden" data-id="element-50">
        <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-51">
          <img src="https://cdn.magicpatterns.com/uploads/8g3wmPGEMmZfgspTMBkDC1/Imagen_fondo_Hero.webp" alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-50" fetchPriority="high" loading="eager" data-id="element-52" />
          <div className="absolute inset-0 bg-cream/50" data-id="element-53" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center text-center gap-10" data-id="element-54">
          <motion.div className="space-y-8 flex flex-col items-center" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-55">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-sage font-bold text-sm border border-sage/40 shadow-md" data-id="element-56">
              <Brain className="w-4 h-4" data-id="element-57" />
              <span data-id="element-58">Basado en Neurociencia y Regulación Emocional</span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-[3.5rem] text-brown leading-[1.15]" data-id="element-59">
              Descubre cómo calmar las rabietas de tu hijo en{' '}
              <span className="text-terracotta italic underline decoration-terracotta/30 underline-offset-4" data-id="element-60">
                menos de 5 minutos
              </span>{' '}
              — sin gritos, castigos ni culpa.
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-brown/70 leading-relaxed max-w-2xl" data-id="element-61">
              Con el{' '}
              <strong className="text-terracotta font-bold" data-id="element-62">
                Método de Regulación Co-activa
              </strong>{' '}
              (basado en neurociencia), recuperarás la calma en casa y la
              conexión con tu peque 💛
            </motion.p>

            <motion.div variants={fadeInUp} className="pt-2 flex flex-col items-center gap-5 w-full" data-id="element-63">
              <Button fullWidth className="max-w-md py-5 text-xl" onClick={() => document.getElementById('pricing')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-64">
                Obtener Acceso Inmediato
              </Button>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-brown/50" data-id="element-65">
                <span className="flex items-center gap-1.5" data-id="element-66">
                  <Clock className="w-4 h-4" data-id="element-67" /> Acceso inmediato
                </span>
                <span className="flex items-center gap-1.5" data-id="element-68">
                  <Lock className="w-4 h-4" data-id="element-69" /> Pago seguro
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div className="w-full max-w-sm relative mt-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: 'easeOut'
        }} data-id="element-70">
            <div className="absolute inset-0 bg-terracotta/8 rounded-3xl blur-2xl scale-110 -z-10" data-id="element-71" />
            <img src="https://cdn.magicpatterns.com/uploads/835kqVkQLoxVcsNCtv7Vzj/Imagen_del_producto.png" alt="El Fin de las Rabietas - Guía para padres en tablet y móvil" className="w-full h-auto rounded-2xl shadow-soft object-cover" fetchPriority="high" loading="eager" data-id="element-72" />
          </motion.div>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="py-12 px-6 bg-white" data-id="element-73">
        <motion.div className="max-w-3xl mx-auto text-center space-y-8" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-74">
          <motion.div variants={fadeInUp} data-id="element-75">
            <Heart className="w-12 h-12 text-terracotta mx-auto mb-6 opacity-80" data-id="element-76" />
            <p className="text-xl md:text-2xl leading-relaxed text-brown font-medium" data-id="element-77">
              "Son las 6 de la tarde. Estás agotada. Y de repente, por un vaso
              de color equivocado o un juguete que no se queda en pie, tu hijo
              estalla. Los gritos llenan la casa, tu paciencia se evapora y
              sientes que, por más que explicas o castigas, nada cambia."
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-16 h-px bg-terracotta/30 mx-auto" data-id="element-78" />

          <motion.p variants={fadeInUp} className="text-lg text-brown/80 leading-relaxed" data-id="element-79">
            No estás sola. Pero lo más importante que leerás hoy es esto: las
            rabietas no son una señal de que eres una "mala madre", son una
            desesperada señal de auxilio del cerebro en desarrollo de tu hijo
            que aún no sabe cómo decirte lo que siente.
          </motion.p>
        </motion.div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-12 px-6 bg-cream-dark" data-id="element-80">
        <div className="max-w-4xl mx-auto" data-id="element-81">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft" data-id="element-82">
            <h2 className="text-3xl md:text-4xl text-center mb-8" data-id="element-83">
              El ciclo de "reacción-culpa"
            </h2>
            <div className="space-y-6 text-lg text-brown/80 leading-relaxed" data-id="element-84">
              <p data-id="element-85">
                La mayoría de las madres viven en un ciclo de "reacción-culpa".
                Intentas razonar con lógica cuando él está en plena crisis (lo
                cual es neurológicamente imposible), terminas gritando porque no
                puedes más, y luego pasas la noche mirando a tu hijo dormir
                sintiéndote la peor persona del mundo.
              </p>
              <p data-id="element-86">
                El problema no es tu falta de autoridad, sino que nos enseñaron
                a criar desde la supresión de emociones ("no llores", "vete a tu
                cuarto", "ahora te vas a enterar"). Esos métodos no enseñan a tu
                hijo a calmarse; solo le enseñan a esconder su dolor por miedo,
                lo que garantiza que la rabieta sea más explosiva la próxima
                vez.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-12 px-6 bg-white" data-id="element-87">
        <div className="max-w-6xl mx-auto" data-id="element-88">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-89">
            <h2 className="text-3xl md:text-5xl mb-6" data-id="element-90">
              Por qué lo que has intentado no funciona
            </h2>
            <p className="text-xl text-brown/70 max-w-2xl mx-auto" data-id="element-91">
              No es tu culpa. Las estrategias tradicionales van en contra de
              cómo funciona el cerebro infantil.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-92">
            {[{
            icon: Brain,
            title: 'Intentar razonar',
            desc: "Falla porque el cerebro lógico de tu hijo se 'desconecta' literalmente durante la crisis; es como hablarle a alguien que se está ahogando."
          }, {
            icon: Flame,
            title: 'Gritar',
            desc: 'Solo añade combustible al fuego, elevando los niveles de cortisol y bloqueando cualquier posibilidad de aprendizaje.'
          }, {
            icon: ShieldAlert,
            title: 'Los castigos',
            desc: 'Generan resentimiento y miedo, pero no construyen las rutas neuronales que tu hijo necesita para el autocontrol futuro.'
          }, {
            icon: EyeOff,
            title: 'Ignorarlo',
            desc: "Solo le confirma que su 'puerto seguro' (tú) desaparece cuando él más te necesita, aumentando su ansiedad."
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-cream p-8 rounded-2xl flex gap-6 items-start" data-id="element-93">
                <div className="bg-white p-4 rounded-full shadow-sm shrink-0" data-id="element-94">
                  <item.icon className="w-8 h-8 text-terracotta" data-id="element-95" />
                </div>
                <div data-id="element-96">
                  <h3 className="text-2xl mb-3" data-id="element-97">{item.title}</h3>
                  <p className="text-brown/70 leading-relaxed" data-id="element-98">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM (Dark Section) */}
      <section className="py-12 px-6 bg-brown text-cream relative overflow-hidden" data-id="element-99">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-terracotta/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-100" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-101">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-102">
            <Sparkles className="w-12 h-12 text-terracotta mx-auto mb-8" data-id="element-103" />
            <h2 className="text-4xl md:text-5xl mb-8 text-white" data-id="element-104">
              Presentamos el Método de los 5 Minutos <br data-id="element-105" />
              <span className="text-terracotta italic text-3xl md:text-4xl" data-id="element-106">
                (Regulación Co-activa)
              </span>
            </h2>
            <div className="space-y-6 text-lg md:text-xl text-cream/80 leading-relaxed font-light" data-id="element-107">
              <p data-id="element-108">No es magia, es neurociencia aplicada a la crianza.</p>
              <p data-id="element-109">
                En lugar de luchar contra la rabieta, este sistema te enseña a
                usar tu propio sistema nervioso como un "ancla" para el de tu
                hijo. A través de una secuencia exacta de 60 segundos por etapa{' '}
                <strong className="text-white font-medium" data-id="element-110">
                  (Pausar, Conectar, Validar, Redirigir y Cerrar)
                </strong>
                , lograrás que el cerebro de tu hijo pase del modo
                "supervivencia" al modo "calma" en tiempo récord.
              </p>
              <p className="text-2xl text-white font-serif pt-4" data-id="element-111">
                No fuerzas la obediencia; facilitas la regulación.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-12 px-6 bg-cream" data-id="element-112">
        <div className="max-w-5xl mx-auto" data-id="element-113">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-114">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-115">
              ¿Qué incluye el método?
            </h2>
            <p className="text-xl text-brown/70" data-id="element-116">
              Todo lo que necesitas para transformar el ambiente de tu hogar hoy
              mismo.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-117">
            {[{
            title: "La Guía Maestra 'Fin de las Rabietas'",
            desc: 'Un paso a paso completo desde la neurociencia hasta la acción práctica.'
          }, {
            title: 'El Mapa de los 5 Minutos',
            desc: 'La secuencia exacta para desescalar cualquier crisis sin importar el lugar.'
          }, {
            title: 'Diccionario de Frases de Validación',
            desc: "Qué decir exactamente para 'nombrar y domesticar' la emoción sin usar el destructivo 'pero...'."
          }, {
            title: 'Protocolo de Emergencia en Público',
            desc: 'Cómo manejar la presión social y el juicio de los extraños sin ceder ante el berrinche.'
          }, {
            title: 'Guía de Reparación',
            desc: 'El plan de 4 pasos para cuando tú pierdes el control y necesitas sanar el vínculo con tu hijo.'
          }, {
            title: 'Estrategia de Prevención',
            desc: "Rutinas sencillas y el método 'una vez y se cumple' para reducir la frecuencia de las rabietas antes de que ocurran."
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-6 rounded-2xl shadow-sm border border-brown/5 flex gap-4" data-id="element-118">
                <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-1" data-id="element-119" />
                <div data-id="element-120">
                  <h4 className="font-serif text-xl mb-2" data-id="element-121">{item.title}</h4>
                  <p className="text-brown/70" data-id="element-122">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-6 bg-white" data-id="element-123">
        <div className="max-w-4xl mx-auto" data-id="element-124">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-12" data-id="element-125">
            {/* Intro */}
            <motion.div variants={fadeInUp} className="text-center space-y-5" data-id="element-126">
              <h2 className="text-4xl md:text-5xl" data-id="element-127">Pero eso no es todo.</h2>
              <p className="text-xl text-brown/70 max-w-2xl mx-auto leading-relaxed" data-id="element-128">
                Cuando actúas hoy incluimos{' '}
                <strong className="text-terracotta" data-id="element-129">
                  5 recursos adicionales valorados en $47
                </strong>{' '}
                — completamente gratis.
              </p>
              <p className="text-lg text-brown/60 max-w-xl mx-auto leading-relaxed" data-id="element-130">
                Porque queremos que tengas TODO lo que necesitas para nunca más
                sentirte sin herramientas en el peor momento.
              </p>
            </motion.div>

            {/* Bonus Cards */}
            <motion.div variants={staggerContainer} className="space-y-6" data-id="element-131">
              {[{
              number: 1,
              title: 'Guía de Recursos Sensoriales STEAM',
              desc: 'Crea tu kit completo por menos de $20 esta tarde.',
              image: 'https://cdn.magicpatterns.com/uploads/k5Xpit1v4dQdPU4b29fDf4/Bono_1.png',
              featured: false
            }, {
              number: 2,
              title: 'Intervención Inmediata',
              desc: '3 experimentos exactos para los primeros 90 segundos de cualquier rabieta.',
              image: 'https://cdn.magicpatterns.com/uploads/uYvFyL3tY3MCiQkiG91PhX/Bono_2.png',
              featured: false
            }, {
              number: 3,
              title: 'El Detective Emocional',
              desc: 'Identifica la causa real de cada rabieta en 10 segundos y actúa con precisión.',
              image: 'https://cdn.magicpatterns.com/uploads/aT79xaNompigEaPv8HWTJr/Bono_3.png',
              featured: true
            }, {
              number: 4,
              title: 'Kit para Rabietas en Público',
              desc: '3 técnicas invisibles para recuperar la calma sin miradas incómodas.',
              image: 'https://cdn.magicpatterns.com/uploads/5299TuU5e9CJ5fmMmaZcph/Bono_4.png',
              featured: false
            }, {
              number: 5,
              title: 'Tracker de Patrones',
              desc: 'El mapa para anticiparte a las rabietas antes de que exploten.',
              image: 'https://cdn.magicpatterns.com/uploads/oEMnX9RLCFHnfrqXLZNw75/Bono_5.png',
              featured: false
            }].map(bonus => <motion.div key={bonus.number} variants={fadeInUp} className={`flex flex-col md:flex-row items-center gap-6 p-6 md:p-8 rounded-2xl border ${bonus.featured ? 'bg-sage/5 border-sage/20 shadow-md' : 'bg-cream border-brown/5'}`} data-id="element-132">
                  <img src={bonus.image} alt={bonus.title} className="w-32 h-40 md:w-36 md:h-44 object-cover rounded-xl shadow-sm shrink-0" loading="lazy" data-id="element-133" />
                  <div className="text-center md:text-left space-y-2" data-id="element-134">
                    <div className="flex items-center justify-center md:justify-start gap-2" data-id="element-135">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-terracotta text-white text-sm font-bold shrink-0" data-id="element-136">
                        {bonus.number}
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wider text-brown/40" data-id="element-137">
                        Bonus #{bonus.number}
                        {bonus.featured && <Star className="inline w-3.5 h-3.5 text-terracotta fill-terracotta ml-1.5 -mt-0.5" data-id="element-138" />}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl text-brown" data-id="element-139">
                      {bonus.title}
                    </h3>
                    <p className="text-brown/60 leading-relaxed" data-id="element-140">
                      {bonus.desc}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>

            {/* Value Summary */}
            <motion.div variants={fadeInUp} className="bg-cream-dark p-8 md:p-10 rounded-3xl border border-brown/10 text-center space-y-4" data-id="element-141">
              <p className="text-brown/50 text-lg" data-id="element-142">
                Valor de los 5 bonos: <span className="line-through" data-id="element-143">$47</span>
              </p>
              <p className="text-3xl md:text-4xl font-serif text-brown font-bold" data-id="element-144">
                Tuyos hoy: GRATIS
              </p>
              <div className="w-16 h-px bg-brown/10 mx-auto" data-id="element-145" />
              <p className="text-brown/60 leading-relaxed max-w-lg mx-auto" data-id="element-146">
                Una sola sesión de terapia ocupacional cuesta entre $60-$120. Tú
                obtienes hoy todo un sistema completo más 5 recursos extra
                valorados en $47 por menos del precio de un café.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-12 px-6 bg-cream" data-id="element-147">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-148">
          {/* For */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-sage/10 p-8 md:p-10 rounded-3xl" data-id="element-149">
            <h3 className="text-3xl mb-8 flex items-center gap-3" data-id="element-150">
              <Heart className="w-8 h-8 text-sage" data-id="element-151" />
              Esto ES para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-152">
              {['Madres con hijos entre 2 y 7 años que se sienten sobrepasadas por la intensidad emocional de sus pequeños.', 'Cuidadores que quieren romper ciclos generacionales de gritos y castigos físicos.', 'Mamás que trabajan y llegan a casa con poca energía, necesitando soluciones efectivas y rápidas.', 'Padres que buscan una crianza basada en el respeto pero que no quieren caer en la permisividad.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-153">
                  <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-0.5" data-id="element-154" />
                  <span className="text-brown/80 leading-relaxed" data-id="element-155">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Not For */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-terracotta/5 p-8 md:p-10 rounded-3xl border border-terracotta/10" data-id="element-156">
            <h3 className="text-3xl mb-8 flex items-center gap-3" data-id="element-157">
              <XCircle className="w-8 h-8 text-terracotta" data-id="element-158" />
              Esto NO es para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-159">
              {["Personas que buscan 'trucos' para manipular o silenciar a los niños rápidamente por miedo.", 'Quienes creen que el castigo físico es la única forma de enseñar respeto.', 'Madres que no están dispuestas a trabajar primero en su propia calma antes de pedirla a sus hijos.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-160">
                  <XCircle className="w-6 h-6 text-terracotta/60 shrink-0 mt-0.5" data-id="element-161" />
                  <span className="text-brown/80 leading-relaxed" data-id="element-162">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 8. HOW IT WORKS & PROOF */}
      <section className="py-12 px-6 bg-cream-dark" data-id="element-163">
        <div className="max-w-4xl mx-auto space-y-20" data-id="element-164">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-165">
            <h2 className="text-4xl text-center mb-12" data-id="element-166">¿Cómo funciona?</h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-brown/10" data-id="element-167">
              {[{
              step: '1',
              title: 'Descarga la Guía',
              desc: 'Recibe acceso inmediato para leerla desde tu celular o tablet hoy mismo.'
            }, {
              step: '2',
              title: 'Aplica el Primer Minuto',
              desc: 'La próxima vez que empiece el caos, aplica la técnica de respiración y pausa que aprenderás en el capítulo 11.'
            }, {
              step: '3',
              title: 'Observa el Cambio',
              desc: 'Mira cómo, al cambiar tu respuesta, la frecuencia y duración de las rabietas disminuye drásticamente en menos de 7 días.'
            }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-168">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream-dark bg-terracotta text-white font-serif text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-md z-10" data-id="element-169">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm" data-id="element-170">
                    <h4 className="font-serif text-2xl mb-2" data-id="element-171">{item.title}</h4>
                    <p className="text-brown/70" data-id="element-172">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft text-center" data-id="element-173">
            <Star className="w-10 h-10 text-terracotta mx-auto mb-6 fill-terracotta/20" data-id="element-174" />
            <p className="text-xl md:text-2xl text-brown leading-relaxed italic font-serif" data-id="element-175">
              "En solo una semana aplicando la regulación co-activa, notarás que
              las explosiones de 20 minutos se convierten en momentos de
              conexión de 5 minutos. No solo las rabietas se detienen más
              rápido; notarás que tu hijo empieza a buscarte para contarte lo
              que siente antes de estallar, porque ahora sabe que tú eres un
              lugar seguro, no un juez."
            </p>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 px-6 bg-white" data-id="element-176">
        <div className="max-w-6xl mx-auto" data-id="element-177">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-178">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-179">
              Lo que dicen otras familias
            </h2>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-180">
            {[{
            name: 'Jessica M.',
            location: 'Houston, TX',
            image: 'https://cdn.magicpatterns.com/uploads/dQDS51MCMuVUPLbVcK2XRi/Jessica_M..webp',
            quote: 'Ay, no saben lo que era mi casa a las 6 de la tarde… un campo de batalla. Mi nena de 3 años se tiraba al piso por TODO. Yo terminaba gritando y después llorando en el baño sintiéndome la peor mamá. Compré esta guía sin mucha esperanza, pero el truco de los 5 minutos me cambió la vida. Ayer mi hija empezó con una rabieta por las galletas y en vez de explotar yo, hice lo que dice el paso 2. Se calmó en menos de 3 minutos. TRES. Yo no lo podía creer. 💛',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Daniela P.',
            location: 'Miami, FL',
            image: 'https://cdn.magicpatterns.com/uploads/m2UocvJukjFXM6UzfC1LNE/Daniela_P..webp',
            quote: 'Les soy honesta, yo era de las que pensaba "a mí me criaron con chancleta y salí bien". Pero ver a mi hijo de 4 años temblando de miedo cuando yo gritaba me rompió el corazón. Una amiga me pasó esta guía y la leí esa misma noche llorando. Lo que más me ayudó fue entender POR QUÉ mi hijo hace lo que hace. No es manipulación, es que su cerebrito no puede más. Ahora cuando empieza una crisis, yo respiro primero y él se calma solito. Mi esposo no lo puede creer.',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Adriana L.',
            location: 'Los Angeles, CA',
            image: 'https://cdn.magicpatterns.com/uploads/fxW4GQ5UtkqDyCTPYK64LC/Adriana_L..webp',
            quote: 'Trabajo doble turno y llego a mi casa destruida. Lo último que necesito es una hora de gritos con mi hijo de 5 años. Esta guía es corta, va directo al punto y las frases que te dan para decirle al niño son INCREÍBLES. Yo antes decía "ya cállate" y ahora digo algo completamente diferente y la reacción de mi hijo es otra. En una semana las rabietas bajaron como un 70%. No es magia, pero se siente como magia. Se las recomiendo a todas las mamás que conozco.',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Carla J.',
            location: 'San Antonio, TX',
            image: 'https://cdn.magicpatterns.com/uploads/cm5YjnwwMpf6TCREGZ2Zrf/Carla_J..webp',
            quote: "Pensé que nada funcionaría con mi hijo porque tiene una personalidad muy fuerte y las rabietas eran extremas, incluso en público. Usé la Estrategia de Emergencia en el supermercado la semana pasada y la diferencia fue increíble. No hubo drama, no hubo miradas de juicio de extraños, solo una conexión real entre nosotros. Si crees que tu hijo es 'imposible', prueba este método.",
            stars: '⭐⭐⭐⭐⭐'
          }].map((t, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl shadow-sm border border-brown/5 flex flex-col gap-6" data-id="element-181">
                <div className="flex items-center gap-4" data-id="element-182">
                  <img src={t.image} alt={t.name} className="w-16 h-16 rounded-full object-cover" loading="lazy" data-id="element-183" />
                  <div data-id="element-184">
                    <h4 className="font-serif text-xl text-brown" data-id="element-185">{t.name}</h4>
                    <p className="text-sm text-brown/60" data-id="element-186">{t.location}</p>
                    <div className="text-sm mt-1" data-id="element-187">{t.stars}</div>
                  </div>
                </div>
                <p className="text-brown/80 leading-relaxed italic" data-id="element-188">
                  "{t.quote}"
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-12 px-6 bg-cream" data-id="element-189">
        <div className="max-w-3xl mx-auto" data-id="element-190">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-191">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-192">Preguntas Frecuentes</h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-white p-6 md:p-10 rounded-3xl shadow-sm" data-id="element-193">
            <FAQItem question="¿Funciona con niños de 2 años?" answer="Sí, está diseñado específicamente para la etapa de desarrollo donde el cerebro emocional es dominante." data-id="element-194" />
            <FAQItem question="¿Qué pasa si mi hijo es muy terco?" answer="No existe la terquedad, existe la necesidad de autonomía. El método enseña cómo darles control sin ceder en tus límites." data-id="element-195" />
            <FAQItem question="¿Y si yo soy la que siempre pierde los nervios?" answer="La guía incluye una sección completa de 'Autocontrol para Mamás' para que tú también sanes tu sistema nervioso." data-id="element-196" />
            <FAQItem question="¿Es mucho texto para leer?" answer="No. Está diseñado para madres con tiempo limitado, con tablas, listas y puntos clave para ir directo a la solución." data-id="element-197" />
          </motion.div>
        </div>
      </section>

      {/* 10. OFFER & CTA */}
      <section ref={pricingRef} id="pricing" className="py-12 px-6 bg-white relative overflow-hidden" data-id="element-198">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cream-dark via-white to-white -z-10" data-id="element-199" />

        <div className="max-w-4xl mx-auto text-center" data-id="element-200">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="space-y-8" data-id="element-201">
            <h2 className="text-4xl md:text-5xl leading-tight" data-id="element-202">
              Transforma la atmósfera de tu hogar hoy mismo.
            </h2>

            <div className="bg-cream-dark p-10 md:p-14 rounded-3xl border border-brown/10 shadow-xl shadow-brown/5 max-w-2xl mx-auto relative" data-id="element-203">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-sage text-white px-6 py-2 rounded-full text-sm font-bold tracking-wider uppercase shadow-md" data-id="element-204">
                Oferta de Lanzamiento
              </div>

              <CountdownTimer data-id="element-205" />

              <div className="flex flex-col items-center gap-3 mt-6 mb-10" data-id="element-206">
                <span className="text-xl text-brown/35 line-through font-serif" data-id="element-207">
                  $27.00 USD
                </span>
                <div className="flex items-baseline gap-2" data-id="element-208">
                  <span className="text-6xl md:text-7xl font-serif font-bold text-brown" data-id="element-209">
                    $6.39
                  </span>
                  <span className="text-xl text-brown/50 font-medium" data-id="element-210">USD</span>
                </div>
              </div>

              <p className="text-lg text-brown/70 mb-10 font-medium tracking-wide" data-id="element-211">
                Pago único. Acceso de por vida.
              </p>

              <p className="text-brown/60 mb-8 leading-relaxed max-w-lg mx-auto" data-id="element-212">
                Obtendrás todas las herramientas para que las tardes vuelvan a
                ser de paz y no de batallas. Es menos de lo que cuesta un café
                especial, pero con un impacto que durará toda la vida de tu
                hijo.
              </p>

              {/* Bonus Summary in Pricing */}
              <div className="bg-white/80 rounded-2xl border border-brown/10 p-6 mb-8 text-left" data-id="element-213">
                <p className="text-sm font-bold uppercase tracking-wider text-brown/40 mb-4 text-center" data-id="element-214">
                  Incluido con tu compra
                </p>
                <ul className="space-y-3" data-id="element-215">
                  <li className="flex items-center gap-3" data-id="element-216">
                    <CheckCircle2 className="w-5 h-5 text-sage shrink-0" data-id="element-217" />
                    <span className="text-brown/80 text-sm" data-id="element-218">
                      El Fin de las Rabietas (Guía Principal)
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-219">
                    <CheckCircle2 className="w-5 h-5 text-sage shrink-0" data-id="element-220" />
                    <span className="text-brown/80 text-sm" data-id="element-221">
                      BONUS 1: Kit Sensorial STEAM
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-222">
                    <CheckCircle2 className="w-5 h-5 text-sage shrink-0" data-id="element-223" />
                    <span className="text-brown/80 text-sm" data-id="element-224">
                      BONUS 2: Intervención Inmediata
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-225">
                    <CheckCircle2 className="w-5 h-5 text-sage shrink-0" data-id="element-226" />
                    <span className="text-brown/80 text-sm" data-id="element-227">
                      BONUS 3: El Detective Emocional
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-228">
                    <CheckCircle2 className="w-5 h-5 text-sage shrink-0" data-id="element-229" />
                    <span className="text-brown/80 text-sm" data-id="element-230">
                      BONUS 4: Kit para Rabietas en Público
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-231">
                    <CheckCircle2 className="w-5 h-5 text-sage shrink-0" data-id="element-232" />
                    <span className="text-brown/80 text-sm" data-id="element-233">
                      BONUS 5: Tracker de Patrones
                    </span>
                  </li>
                </ul>
                <p className="text-center text-sm text-terracotta font-bold mt-4" data-id="element-234">
                  Valor de los 5 bonos:{' '}
                  <span className="line-through text-brown/40" data-id="element-235">$47</span> —
                  GRATIS hoy
                </p>

                {/* Expandable bonus details */}
                <PricingBonusDetails data-id="element-236" />
              </div>

              <Button fullWidth className="px-8 py-6 md:py-7 text-xl" onClick={handlePurchaseCTA} data-id="element-237">
                QUIERO EL MÉTODO FIN DE LAS RABIETAS AHORA
              </Button>

              <div className="mt-10 flex flex-col items-center gap-3 p-5 bg-white/80 rounded-2xl border border-sage/15" data-id="element-238">
                <ShieldCheck className="w-9 h-9 text-sage/80" data-id="element-239" />
                <p className="text-brown/80 font-serif text-base font-medium" data-id="element-240">
                  Garantía de Satisfacción — 7 días
                </p>
                <p className="text-brown/50 text-sm text-center leading-relaxed max-w-sm" data-id="element-241">
                  Si no ves resultados aplicando el método, te devolvemos el
                  100% de tu dinero. Sin preguntas.
                </p>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-brown/40" data-id="element-242">
                <Lock className="w-4 h-4" data-id="element-243" />
                <span data-id="element-244">Pago 100% seguro. Acceso inmediato tras la compra.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center text-brown/40 text-sm bg-white border-t border-brown/5 pb-24 md:pb-8" data-id="element-245">
        <p data-id="element-246">
          © {new Date().getFullYear()} El Fin de las Rabietas. Todos los
          derechos reservados.
        </p>
      </footer>

      <SalesNotification data-id="element-247" />
    </div>;
}